import React, { useEffect, useState } from 'react'

interface SwitchProps {
    label: string
    checked?: boolean
    defaultChecked?: boolean
    disabled?: boolean
    onChange?: (checked: boolean) => void
    color?: 'blue' | 'gray'
}

const Switch: React.FC<SwitchProps> = ({
    label,
    checked,
    defaultChecked = false,
    disabled = false,
    onChange,
    color = 'blue'
}) => {
    const [internalChecked, setInternalChecked] = useState(defaultChecked)
    const isControlled = checked !== undefined
    const isChecked = isControlled ? checked : internalChecked

    useEffect(() => {
        if (!isControlled) setInternalChecked(defaultChecked)
    }, [defaultChecked, isControlled])

    const handleToggle = () => {
        if (disabled) return
        const nextChecked = !isChecked
        if (!isControlled) setInternalChecked(nextChecked)
        onChange?.(nextChecked)
    }

    const activeBackground = color === 'blue' ? 'bg-brand-500' : 'bg-gray-800 dark:bg-white/30'

    return (
        <label
            className={`flex select-none items-center gap-3 text-sm font-medium ${
                disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700 dark:text-gray-400'
            }`}
        >
            <button
                type='button'
                role='switch'
                aria-checked={isChecked}
                aria-label={label}
                disabled={disabled}
                onClick={handleToggle}
                className={`relative h-6 w-11 rounded-full transition duration-150 ease-linear ${
                    disabled
                        ? 'bg-gray-100 dark:bg-gray-800'
                        : isChecked
                          ? activeBackground
                          : 'bg-gray-200 dark:bg-white/10'
                }`}
            >
                <span
                    className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-theme-sm transition-transform duration-150 ${
                        isChecked ? 'translate-x-full' : 'translate-x-0'
                    }`}
                />
            </button>
            {label && <span>{label}</span>}
        </label>
    )
}

export default Switch
